import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ADMIN_MFA_REQUIRED, auth, db, getHttpFunctionUrl, postJsonWithAuth } from './firebase'; 
import { processarAlteracaoPlanoAdministrativo } from './alteracaoPlanoAdministrativo';
import { signOut } from 'firebase/auth';
import { collection, addDoc, onSnapshot, deleteDoc, updateDoc, doc, query, where, orderBy, limit, getDocs, startAfter, startAt, endAt, setDoc, serverTimestamp, writeBatch, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import AbaVitrinePlanos from './components/AbaVitrinePlanos';
import AbaConfiguracoes from './components/AbaConfiguracoes';
import AbaPostagemLote from './components/AbaPostagemLote';
import AbaMeuCatalogo from './components/AbaMeuCatalogo';
import AbaUsuarios from './components/AbaUsuarios';
import AbaAprovacoes from './components/AbaAprovacoes';
import AbaModeracao from './components/AbaModeracao';
import AbaTags from './components/AbaTags';
import AbaRelatorios from './components/AbaRelatorios';
import AbaLimpeza from './components/AbaLimpeza';
import AbaAfiliados from './components/AbaAfiliados';
import AbaMinhasFiliais from './components/AbaMinhasFiliais'; // RESTAURADO
import AbaFinanceiro from './components/AbaFinanceiro';
import AbaClientes from './components/AbaClientes';
import imageCompression from 'browser-image-compression';
import AbaGestaoLocais from './components/AbaGestaoLocais';
import AbaManutencaoLojas from './components/AbaManutencaoLojas';
import AbaInicioPainel from './components/AbaInicioPainel';
import ModalReautenticacao from './components/ModalReautenticacao';
import ModalFaturaGerada from './components/ModalFaturaGerada';
import BotaoAjuda from './components/BotaoAjuda';

const CORRECOES_NOMES_TAG = {
  'Fármacia': 'Farmácia',
};

const TAMANHO_PAGINA_CATALOGO = 5;
const LIMITE_SUGESTOES_CATALOGO = 8;
const REAUTH_ADMIN_WINDOW_MS = 15 * 60 * 1000;
const MFA_ADMIN_DEFAULT = ADMIN_MFA_REQUIRED;

const montarUrlFuncaoHttp = getHttpFunctionUrl;

function normalizarNomeTag(nomeTag) {
  const nome = String(nomeTag || '').trim();
  return CORRECOES_NOMES_TAG[nome] || nome;
}

function ordenarTagsAlfabeticamente(tags = []) {
  return [...tags].sort((a, b) =>
    String(a?.nome_tag || '').localeCompare(String(b?.nome_tag || ''), 'pt-BR', { sensitivity: 'base' }),
  );
}

function normalizarTermoCatalogo(valor) {
  return String(valor || '').trim().toLocaleLowerCase('pt-BR');
}

// Função matemática para calcular a distância em KM entre dois pontos GPS
const calcularDistanciaKM = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 9999;
  const R = 6371; // Raio da Terra em KM
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

function PanelIcon({ name }) {
  const iconProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  };

  switch (name) {
    case 'brand':
      return (
        <svg {...iconProps}>
          <path d="M12 3l7 4v10l-7 4-7-4V7l7-4z" />
          <path d="M9 10h6" />
          <path d="M9 14h4" />
        </svg>
      );
    case 'batch':
      return (
        <svg {...iconProps}>
          <rect x="4" y="5" width="6" height="6" rx="1.5" />
          <rect x="14" y="5" width="6" height="6" rx="1.5" />
          <rect x="4" y="13" width="6" height="6" rx="1.5" />
          <path d="M14 16h6" />
          <path d="M17 13v6" />
        </svg>
      );
    case 'home':
      return (
        <svg {...iconProps}>
          <path d="M3 11.5L12 4l9 7.5" />
          <path d="M5.5 10.5V20h13v-9.5" />
          <path d="M10 20v-5h4v5" />
        </svg>
      );
    case 'catalog':
      return (
        <svg {...iconProps}>
          <path d="M6 4h10a2 2 0 0 1 2 2v12l-5-3-5 3V6a2 2 0 0 0-2-2z" />
          <path d="M6 4a2 2 0 0 0-2 2v12l4-2.4" />
        </svg>
      );
    case 'branches':
      return (
        <svg {...iconProps}>
          <path d="M12 5v14" />
          <path d="M12 8h6v4h-6" />
          <path d="M12 12H6v4h6" />
          <path d="M4 19h4" />
          <path d="M16 19h4" />
        </svg>
      );
    case 'finance':
      return (
        <svg {...iconProps}>
          <path d="M12 3v18" />
          <path d="M16.5 7.5c0-1.9-2-3.5-4.5-3.5S7.5 5.6 7.5 7.5 9 10 12 10s4.5 1.5 4.5 3.5S14.5 17 12 17s-4.5-1.6-4.5-3.5" />
        </svg>
      );
    case 'plans':
      return (
        <svg {...iconProps}>
          <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.4 6.5 20.2l1-6.2L3 9.6l6.2-.9L12 3z" />
        </svg>
      );
    case 'insights':
      return (
        <svg {...iconProps}>
          <path d="M4 19h16" />
          <path d="M7 15l3-3 3 2 4-5" />
          <path d="M17 9h2v2" />
        </svg>
      );
    case 'locations':
      return (
        <svg {...iconProps}>
          <path d="M12 20s6-5.4 6-10a6 6 0 1 0-12 0c0 4.6 6 10 6 10z" />
          <circle cx="12" cy="10" r="2.3" />
        </svg>
      );
    case 'stores':
      return (
        <svg {...iconProps}>
          <path d="M4 10l1.5-5h13L20 10" />
          <path d="M5 10v9h14v-9" />
          <path d="M9 19v-5h6v5" />
        </svg>
      );
    case 'users':
      return (
        <svg {...iconProps}>
          <path d="M16 19a4 4 0 0 0-8 0" />
          <circle cx="12" cy="9" r="3" />
          <path d="M19 19a3 3 0 0 0-2.2-2.9" />
          <path d="M5.2 16.1A3 3 0 0 0 3 19" />
        </svg>
      );
    case 'approvals':
      return (
        <svg {...iconProps}>
          <path d="M20 7L9 18l-5-5" />
        </svg>
      );
    case 'moderation':
      return (
        <svg {...iconProps}>
          <path d="M12 3l7 4v5c0 4.2-2.8 7.8-7 9-4.2-1.2-7-4.8-7-9V7l7-4z" />
          <path d="M9.5 12.5l1.5 1.5 3.5-3.5" />
        </svg>
      );
    case 'tags':
      return (
        <svg {...iconProps}>
          <path d="M20 12l-8 8-8-8V4h8l8 8z" />
          <circle cx="8.5" cy="8.5" r="1.3" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...iconProps}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.2a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 0 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.2a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3h.1A1.7 1.7 0 0 0 10 3.2V3a2 2 0 1 1 4 0v.2a1.7 1.7 0 0 0 1 1.5h.1a1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.2a1.7 1.7 0 0 0-1.4 1z" />
        </svg>
      );
    case 'affiliates':
      return (
        <svg {...iconProps}>
          <path d="M10 14l-2 2a3 3 0 0 1-4.2-4.2l2-2" />
          <path d="M14 10l2-2a3 3 0 0 1 4.2 4.2l-2 2" />
          <path d="M8 12h8" />
        </svg>
      );
    case 'cleanup':
      return (
        <svg {...iconProps}>
          <path d="M4 19h13" />
          <path d="M15 5h5l-3 4v10" />
          <path d="M8 5V3h4v2" />
          <path d="M6 5l1 14h6l1-14" />
        </svg>
      );
    case 'warning':
      return (
        <svg {...iconProps}>
          <path d="M12 3l9 16H3L12 3z" />
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
        </svg>
      );
    default:
      return (
        <svg {...iconProps}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
}

function Dashboard() {
  const [abaAtiva, setAbaAtiva] = useState(''); 
  const [usuarioAtual, setUsuarioAtual] = useState(null);
  const panelContentSectionRef = useRef(null);
  const ignorarPrimeiraAncoraRef = useRef(true);
  
  // Lógica de bloqueio financeiro.
  const estaInadimplente = usuarioAtual?.status_financeiro === 'inadimplente';
  const estaEmCarencia = usuarioAtual?.status_financeiro === 'em_carencia';
  const isFilial = !!usuarioAtual?.id_usuario_pai; // Se tem pai, ? filial

  useEffect(() => {
    // A matriz inadimplente abre o Financeiro; a filial fica na tela bloqueada.
    if (estaInadimplente) {
      setAbaAtiva(isFilial ? 'bloqueado_filial' : 'financeiro');
    }
  }, [estaInadimplente, isFilial]);

  useEffect(() => {
    if (!abaAtiva) {
      return;
    }

    if (ignorarPrimeiraAncoraRef.current) {
      ignorarPrimeiraAncoraRef.current = false;
      return;
    }

    panelContentSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [abaAtiva]);

  // Cotas
  const [postagensWebNaSemana, setPostagensWebNaSemana] = useState(0);
  const [postagensMensaisGeral, setPostagensMensaisGeral] = useState(0);

  // --- ESTADOS DE DADOS ---
  const [tags, setTags] = useState([]);
  const [novaTag, setNovaTag] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const [textoEditado, setTextoEditado] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [promocoes, setPromocoes] = useState([]);
  const [promocaoSelecionada, setPromocaoSelecionada] = useState(null);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const ultimoDocumentoModeracaoRef = useRef(null);
  const carregamentoModeracaoEmAndamentoRef = useRef(false);
  const [temMaisDados, setTemMaisDados] = useState(true);
  
  const [minhasFiliais, setMinhasFiliais] = useState([]); 

  // Configurações.
  const [configGerais, setConfigGerais] = useState({
    aba_internet_ativa: false,
    exibir_premium: true,
    lojistas_veem_lojistas: false,
    lojistas_comentam: false,
    limite_mensal_gratuito: 300,
    limite_gratuito: 10,
    limite_bronze: 20,
    preco_bronze: 49.90,
    preco_prata: 89.90,
    preco_ouro: 149.90,
    dias_limpeza_postagens: 3,
    mfa_admin_obrigatorio: MFA_ADMIN_DEFAULT,
    reautenticacao_lojista_mudar_plano: false,
    reautenticacao_lojista_cancelar_assinatura: false,
    nao_gerar_cobranca_pequena_filial: true,
  });

  const [mostrarTermos, setMostrarTermos] = useState(false);
  const [mostrarPrivacidade, setMostrarPrivacidade] = useState(false);
  const [monitorResumo, setMonitorResumo] = useState(null);
  const [monitoramentoCarregando, setMonitoramentoCarregando] = useState(false);
  const [monitoramentoRetorno, setMonitoramentoRetorno] = useState({ tipo: '', mensagem: '' });
  const [modalReautenticacaoInternaAberto, setModalReautenticacaoInternaAberto] = useState(false);
  const [faturaPlanoParaAbrir, setFaturaPlanoParaAbrir] = useState(null);
  const [acaoInternaPendente, setAcaoInternaPendente] = useState(null);
  const [ultimaReautenticacaoInternaAt, setUltimaReautenticacaoInternaAt] = useState(0);
  const rejeitarLimpezaPendente = useRef(null);

  const [configAfiliados, setConfigAfiliados] = useState({});

  const [opcoesLimpeza, setOpcoesLimpeza] = useState({ promocoes: false, promocoes_internet: false, usuarios: false, alertas: false });
  const [loteProdutos, setLoteProdutos] = useState([]);
  const [isEnviandoLote, setIsEnviandoLote] = useState(false);
  const [locaisDoLote, setLocaisDoLote] = useState([]);
  const [localSelecionadoNoLoteId, setLocalSelecionadoNoLoteId] = useState('');

  const [progressoEnvio, setProgressoEnvio] = useState('');

  // Função do botão "Aplicar a Todas".
  const aplicarDataATodos = (dataEscolhida) => {
    setLoteProdutos(prev => prev.map(p => ({ ...p, data_validade: dataEscolhida })));
  };

  const [produtosPadrao, setProdutosPadrao] = useState([]);
  const [produtosCarregados, setProdutosCarregados] = useState(false);
  const [ultimoProdutoPadrao, setUltimoProdutoPadrao] = useState(null);
  const [temMaisProdutosPadrao, setTemMaisProdutosPadrao] = useState(false);
  const [carregandoProdutosPadrao, setCarregandoProdutosPadrao] = useState(false);
  const [filtroCatalogo, setFiltroCatalogo] = useState({ termo: '', categoriaId: '' });
  const [erroCatalogo, setErroCatalogo] = useState('');
  const [novoProdutoPadrao, setNovoProdutoPadrao] = useState({ nome_produto: '', id_tag: '', imagem: null });
  const iniciarEdicaoProduto = (produto) => {
    setNovoProdutoPadrao({
      id: produto.id,
      nome_produto: produto.nome_produto,
      id_tag: produto.id_tag,
      imagem: null, 
      // Captura segura: tenta pegar url_imagem ou url_foto (compatibilidade)
      url_imagem: produto.url_imagem || produto.url_foto || "" 
    });
  };

const cancelarEdicao = () => {
    setNovoProdutoPadrao({ nome_produto: '', id_tag: '', imagem: null, url_imagem: null });
    
    // Só limpa o campo quando ele também existe na tela.
    const input = document.getElementById('file-produto-padrao');
    if (input) {
      input.value = '';
    }
  };
  const [isSalvandoProduto, setIsSalvandoProduto] = useState(false);

  const [pendenciasCarregadas, setPendenciasCarregadas] = useState(false);

  const consultarPainelAdmin = useCallback(async (payload) => {
    const resposta = await postJsonWithAuth(montarUrlFuncaoHttp('gerenciarUsuariosPainel'), payload);
    const dadosRetorno = await resposta.json().catch(() => ({}));

    if (!resposta.ok || !dadosRetorno?.sucesso) {
      throw new Error(dadosRetorno?.erro || 'Não foi possível carregar os dados do painel agora.');
    }

    return dadosRetorno;
  }, []);

  const gerenciarLimpezaPainel = useCallback(async (payload) => {
    const executar = async () => (await consultarPainelAdmin(payload)).cleanup;
    if (payload.action !== 'execute_cleanup' || Date.now() - ultimaReautenticacaoInternaAt < REAUTH_ADMIN_WINDOW_MS) return executar();
    return new Promise((resolve, reject) => {
      rejeitarLimpezaPendente.current = reject;
      setAcaoInternaPendente(() => async () => {
        try { resolve(await executar()); } catch (error) { reject(error); }
        finally { rejeitarLimpezaPendente.current = null; }
      });
      setModalReautenticacaoInternaAberto(true);
    });
  }, [consultarPainelAdmin, ultimaReautenticacaoInternaAt]);

  const gerenciarExclusaoPainel = useCallback(async (payload) => {
    if (payload.action !== 'delete_account_request' || Date.now() - ultimaReautenticacaoInternaAt < REAUTH_ADMIN_WINDOW_MS) return consultarPainelAdmin(payload);
    return new Promise((resolve, reject) => {
      rejeitarLimpezaPendente.current = reject;
      setAcaoInternaPendente(() => async () => {
        try { resolve(await consultarPainelAdmin(payload)); } catch (error) { reject(error); }
        finally { rejeitarLimpezaPendente.current = null; }
      });
      setModalReautenticacaoInternaAberto(true);
    });
  }, [consultarPainelAdmin, ultimaReautenticacaoInternaAt]);

  const handleAfiliadosChange = async (c, v) => { 
    try { 
      await setDoc(doc(db, "configuracoes", "afiliados"), { [c]: v }, { merge: true }); 
      setConfigAfiliados(prev => ({...prev, [c]: v})); 
    } catch (e) { console.error(e); } 
  };

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    let cancelado = false;
    let vigiaUsuarioAtual = () => {};
    let vigiaCotaSemanal = () => {};
    let vigiaCotaMensal = () => {};
    let vigiaFiliais = () => {};
    let matrizObservadaNasFiliais = null;

    const carregarConfigETagsSeguros = async (perfisAtivos = []) => {
      try {
        const isAdminPerfil = perfisAtivos.includes('admin');
        const isTechnicalPerfil = perfisAtivos.includes('tecnico');
        const podeConsultarAdministrativo = isAdminPerfil || isTechnicalPerfil;
        const [snapGerais, snapTags] = await Promise.all([
          getDoc(doc(db, "configuracoes", "gerais")),
          getDocs(query(collection(db, "tags"), orderBy('nome_tag', 'asc'))),
        ]);

        if (snapGerais.exists()) {
          const dadosGerais = snapGerais.data() || {};
          const dadosGeraisNormalizados = {
            ...dadosGerais,
            mfa_admin_obrigatorio: ADMIN_MFA_REQUIRED
              ? true
              : Boolean(dadosGerais.mfa_admin_obrigatorio),
          };
          setConfigGerais(prev => ({ ...prev, ...dadosGeraisNormalizados }));
        }

        const tagsCarregadas = snapTags.docs.map((docSnap) => {
          const dados = docSnap.data() || {};
          return {
            id: docSnap.id,
            ...dados,
            nome_tag: normalizarNomeTag(dados.nome_tag),
          };
        });
        const tagsCorrigidas = tagsCarregadas.filter((tag, index) => {
          const nomeOriginal = snapTags.docs[index].data()?.nome_tag;
          return tag.nome_tag && tag.nome_tag !== nomeOriginal;
        });

        if (tagsCorrigidas.length > 0 && isAdminPerfil) {
          await Promise.all(tagsCorrigidas.map((tag) =>
            updateDoc(doc(db, 'tags', tag.id), { nome_tag: tag.nome_tag }),
          ));
        }

        setTags(ordenarTagsAlfabeticamente(tagsCarregadas));

        if (podeConsultarAdministrativo) {
          const [snapAfiliados] = await Promise.all([
            getDoc(doc(db, "configuracoes", "afiliados")),
          ]);

          if (snapAfiliados.exists()) {
            setConfigAfiliados(prev => ({ ...prev, ...snapAfiliados.data() }));
          }
        }
      } catch (e) {
        console.error("Erro ao carregar configurações ou tags:", e);
      }
    };

    const iniciarPainel = async () => {
      vigiaUsuarioAtual = onSnapshot(doc(db, "users", uid), (docSnap) => {
        if (docSnap.exists()) {
          const dados = { id: docSnap.id, ...docSnap.data() };
          const perfis = dados.perfis_ativos || [];

          if (!perfis.includes('admin') && !perfis.includes('lojista') && !perfis.includes('moderador') && !perfis.includes('tecnico')) {
            alert("Acesso Negado: Este painel é exclusivo para Lojistas e Administradores.");
            signOut(auth);
            return;
          }

          if (dados.is_ativo === false) {
            alert('Sua conta foi bloqueada. Entre em contato com a administração para regularizar o acesso.');
            signOut(auth);
            return;
          }
          if (!dados.id_usuario_pai && perfis.includes('lojista')) {
            if (matrizObservadaNasFiliais !== uid) {
              vigiaFiliais();
              matrizObservadaNasFiliais = uid;
              vigiaFiliais = onSnapshot(
                query(collection(db, 'users'), where('id_usuario_pai', '==', uid)),
                (snapFiliais) => {
                  if (cancelado) return;
                  setMinhasFiliais(snapFiliais.docs.map((docFilial) => ({
                    id: docFilial.id,
                    ...docFilial.data(),
                  })));
                },
                (erro) => {
                  console.error("Erro ao observar filiais:", erro);
                  setMinhasFiliais([]);
                },
              );
            }
          } else {
            vigiaFiliais();
            vigiaFiliais = () => {};
            matrizObservadaNasFiliais = null;
            setMinhasFiliais([]);
          }

          setUsuarioAtual(dados);
          setAbaAtiva((atual) => {
            if (!atual) {
              return 'inicio';
            }
            return atual;
          });
          carregarConfigETagsSeguros(perfis);
        }
      }, (erro) => {
        console.error("Erro ao observar usuário atual:", erro);
      });

      carregarConfigETagsSeguros();
    };

    void iniciarPainel();

    return () => {
      cancelado = true;
      vigiaUsuarioAtual();
      vigiaCotaSemanal();
      vigiaCotaMensal();
      vigiaFiliais();
    }; 
  }, [consultarPainelAdmin]);

  // Lê os hodômetros diretamente do perfil para atualizar a tela imediatamente.
  useEffect(() => {
    if (usuarioAtual) {
      const agora = new Date();
      const mesAtual = `${String(agora.getMonth() + 1).padStart(2, '0')}-${agora.getFullYear()}`;
      
      const domingo = new Date(agora);
      domingo.setDate(agora.getDate() - agora.getDay());
      const semanaAtual = `${String(domingo.getDate()).padStart(2, '0')}-${String(domingo.getMonth() + 1).padStart(2, '0')}-${domingo.getFullYear()}`;

      if (usuarioAtual.mes_referencia === mesAtual) {
        setPostagensMensaisGeral(usuarioAtual.postagens_gastas || 0);
      } else {
        setPostagensMensaisGeral(0);
      }

      if (usuarioAtual.semana_referencia === semanaAtual) {
        setPostagensWebNaSemana(usuarioAtual.postagens_gastas_semana || 0);
      } else {
        setPostagensWebNaSemana(0);
      }
    }
  }, [usuarioAtual]);

 const isAdmin = usuarioAtual?.perfis_ativos?.includes('admin');
  const isTechnical = usuarioAtual?.perfis_ativos?.includes('tecnico');
  const isModerator = usuarioAtual?.perfis_ativos?.includes('moderador');
  const isLojista = usuarioAtual?.perfis_ativos?.includes('lojista');
  const planoAtual = usuarioAtual?.plano || 'gratuito';
  const planoAtualNormalizado = String(planoAtual || '').toLowerCase();
  const podeUsarCatalogo = isAdmin || (isLojista && ['prata', 'ouro', 'diamante'].includes(planoAtualNormalizado));
  const podeOperarCatalogo = isAdmin || isLojista;
  const podeOperarPublicacao = isAdmin || isTechnical || isLojista;
  const isPrivilegedReader = isAdmin || isTechnical;
  const podeGerirUsuarios = isAdmin;
  const podeVerUsuarios = isPrivilegedReader;
  const podeGerirAprovacoes = isAdmin || isTechnical;
  const podeVerAprovacoes = isPrivilegedReader;
  const podeModerarConteudo = isAdmin || isModerator;
  const podeVerModeracao = isAdmin || isModerator || isTechnical;
  const podeGerirTags = isAdmin;
  const podeVerTags = isPrivilegedReader;
  const podeGerirConfiguracoes = isAdmin;
  const podeVerConfiguracoes = isAdmin;
  const podeGerirAfiliados = isAdmin;
  const podeVerAfiliados = isAdmin;
  const podeGerirLimpeza = isAdmin;
  const podeVerLimpeza = isAdmin;
  const podeGerirLocais = isAdmin || isTechnical;
  const podeVerLocais = isPrivilegedReader;
  const podeGerirManutencaoLojas = isAdmin || isTechnical;

  useEffect(() => {
    if (!(isAdmin || isTechnical) || abaAtiva !== 'postagemLote') {
      return undefined;
    }

    let ativo = true;
    getDocs(query(collection(db, 'locais'), orderBy('nome', 'asc')))
      .then((snapshot) => {
        if (!ativo) return;
        setLocaisDoLote(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      })
      .catch((erro) => {
        console.error('Erro ao carregar lojas para o lote administrativo:', erro);
        if (ativo) setLocaisDoLote([]);
      });

    return () => {
      ativo = false;
    };
  }, [abaAtiva, isAdmin, isTechnical]);
  const podeVerManutencaoLojas = isPrivilegedReader;
  
  // Apenas matriz Ouro ou Diamante pode acessar os relatórios.
  const podeVerRelatorios = isAdmin || (!isTechnical && isLojista && !isFilial && (planoAtual?.toLowerCase() === 'ouro' || planoAtual?.toLowerCase() === 'diamante'));


  let cotaMaxWeb = 0;
  if (planoAtual === 'gratuito') cotaMaxWeb = configGerais.limite_gratuito || 10;
  else if (planoAtual === 'bronze') cotaMaxWeb = configGerais.limite_bronze || 20;
  else cotaMaxWeb = 999999; 

  const carregarResumoMonitoramento = async () => {
    if (!podeVerConfiguracoes) return;

    setMonitoramentoCarregando(true);
    try {
      const resultado = await consultarPainelAdmin({ action: 'get_monitoring_summary' });
      setMonitorResumo(resultado?.summary || null);
      setMonitoramentoRetorno({
        tipo: 'success',
        mensagem: 'Monitoramento carregado com sucesso. Os eventos operacionais seguem uma janela de 24 horas.',
      });
    } catch (error) {
      console.error("Erro ao carregar monitoramento operacional:", error);
      setMonitorResumo(null);
    } finally {
      setMonitoramentoCarregando(false);
    }
  };

  const atualizarMonitoramentoAgora = async () => {
    if (!isAdmin) return;

    setMonitoramentoRetorno({ tipo: '', mensagem: '' });
    setMonitoramentoCarregando(true);
    try {
      const idProjeto = auth.app.options.projectId || 'promoalert-11788';
      const urlFuncao = `https://us-central1-${idProjeto}.cloudfunctions.net/forcarResumoMonitoramentoOperacional`;
      const resposta = await postJsonWithAuth(urlFuncao, {});
      const resultado = await resposta.json();

      if (!resposta.ok || resultado?.sucesso === false) {
        throw new Error(resultado?.erro || 'Falha ao atualizar o monitoramento.');
      }

      const dadosMonitoramento = await consultarPainelAdmin({ action: 'get_monitoring_summary' });
      setMonitorResumo(dadosMonitoramento?.summary || null);
      setMonitoramentoRetorno({
        tipo: 'success',
        mensagem: 'Monitoramento atualizado com sucesso. Os eventos operacionais seguem uma janela de 24 horas.',
      });
    } catch (error) {
      console.error("Erro ao atualizar monitoramento operacional:", error);
      setMonitoramentoRetorno({
        tipo: 'error',
        mensagem: `Não foi possível atualizar o monitoramento agora: ${error.message}`,
      });
      alert(`Não foi possível atualizar o monitoramento agora: ${error.message}`);
    } finally {
      setMonitoramentoCarregando(false);
    }
  };

  useEffect(() => {
    // Uma troca de conta não pode reaproveitar destinos escolhidos pela conta anterior.
    setLoteProdutos([]);
    setProdutosPadrao([]);
    setProdutosCarregados(false);
    setUltimoProdutoPadrao(null);
    setTemMaisProdutosPadrao(false);
    setFiltroCatalogo({ termo: '', categoriaId: '' });
    setErroCatalogo('');
  }, [usuarioAtual?.id, usuarioAtual?.id_usuario_pai]);

  useEffect(() => {
    if (abaAtiva === 'postagens' && podeVerModeracao) buscarPromocoes(true);
    if (abaAtiva === 'configuracoes' && podeVerConfiguracoes) carregarResumoMonitoramento();
    
    if (abaAtiva === 'meusProdutos' && usuarioAtual && podeUsarCatalogo) {
      buscarProdutosDoLojista();
    }

    if (abaAtiva === 'aprovacoes' && podeVerAprovacoes) {
      buscarTodosUsuarios();
    }
  }, [abaAtiva, usuarioAtual, planoAtual, podeUsarCatalogo, podeVerUsuarios, podeVerAprovacoes, podeVerConfiguracoes, podeVerModeracao, produtosCarregados]);

  const buscarTodosUsuarios = async () => {
    if (pendenciasCarregadas) return;
    try {
      const resultado = await consultarPainelAdmin({
        action: 'list_pending_users',
        pageSize: 150,
      });
      setUsuarios(Array.isArray(resultado?.users) ? resultado.users : []);
      setPendenciasCarregadas(true);
    } catch (e) { console.error(e); }
  };

  const buscarProdutosDoLojista = async ({ termo = '', categoriaId = '', proximaPagina = false, forcar = false } = {}) => {
    if (!usuarioAtual || !podeUsarCatalogo || !podeOperarCatalogo || carregandoProdutosPadrao) return;

    const idDonoDoCatalogo = usuarioAtual.id || auth.currentUser?.uid;
    if (!idDonoDoCatalogo) return;

    const termoNormalizado = normalizarTermoCatalogo(termo);
    const categoriaNormalizada = String(categoriaId || '').trim();
    const consultaAtual = filtroCatalogo.termo === termoNormalizado && filtroCatalogo.categoriaId === categoriaNormalizada;

    if (proximaPagina && (!consultaAtual || !ultimoProdutoPadrao || !temMaisProdutosPadrao)) return;
    if (!proximaPagina && produtosCarregados && consultaAtual && !forcar) return;

    setCarregandoProdutosPadrao(true);
    setErroCatalogo('');
    try {
      const restricoes = [
        where('id_lojista', '==', idDonoDoCatalogo),
      ];

      if (categoriaNormalizada) {
        restricoes.push(where('id_tag', '==', categoriaNormalizada));
      }

      restricoes.push(orderBy('nome_busca', 'asc'));

      if (termoNormalizado && !proximaPagina) {
        restricoes.push(startAt(termoNormalizado));
      }

      if (termoNormalizado) {
        restricoes.push(endAt(`${termoNormalizado}\uf8ff`));
      }

      if (proximaPagina) {
        restricoes.push(startAfter(ultimoProdutoPadrao));
      }

      restricoes.push(limit(TAMANHO_PAGINA_CATALOGO));

      const snap = await getDocs(query(collection(db, 'produtos_padrao'), ...restricoes));
      const listaProdutos = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));

      setProdutosPadrao((atual) => (proximaPagina ? [...atual, ...listaProdutos] : listaProdutos));
      setUltimoProdutoPadrao(snap.docs[snap.docs.length - 1] || null);
      setTemMaisProdutosPadrao(snap.docs.length === TAMANHO_PAGINA_CATALOGO);
      setFiltroCatalogo({ termo: termoNormalizado, categoriaId: categoriaNormalizada });
      setProdutosCarregados(true);
    } catch (error) {
      console.error('Erro ao carregar catálogo paginado:', error);
      setErroCatalogo('Não foi possível carregar o catálogo agora. Tente novamente.');
    } finally {
      setCarregandoProdutosPadrao(false);
    }
  };

  const buscarSugestoesCatalogo = useCallback(async ({ termo = '', categoriaId = '' } = {}) => {
    if (!podeUsarCatalogo || !podeOperarCatalogo) return [];

    const idDonoDoCatalogo = usuarioAtual?.id || auth.currentUser?.uid;
    if (!idDonoDoCatalogo) return [];

    const termoNormalizado = normalizarTermoCatalogo(termo);
    const categoriaNormalizada = String(categoriaId || '').trim();
    const restricoes = [where('id_lojista', '==', idDonoDoCatalogo)];

    if (categoriaNormalizada) {
      restricoes.push(where('id_tag', '==', categoriaNormalizada));
    }

    restricoes.push(orderBy('nome_busca', 'asc'));

    if (termoNormalizado) {
      restricoes.push(startAt(termoNormalizado), endAt(`${termoNormalizado}\uf8ff`));
    }

    restricoes.push(limit(LIMITE_SUGESTOES_CATALOGO));

    const snap = await getDocs(query(collection(db, 'produtos_padrao'), ...restricoes));
    return snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  }, [podeOperarCatalogo, podeUsarCatalogo, usuarioAtual]);

  const filtrarProdutosDoCatalogo = ({ termo = '', categoriaId = '' } = {}) =>
    buscarProdutosDoLojista({ termo, categoriaId, forcar: true });

  const carregarMaisProdutosDoCatalogo = () =>
    buscarProdutosDoLojista({ ...filtroCatalogo, proximaPagina: true });

  const handleNovoProdutoChange = (field, value) => {
    setNovoProdutoPadrao(prev => ({ ...prev, [field]: value }));
  };

 const salvarProdutoPadrao = async (e) => {
    e.preventDefault();

    if (!podeOperarCatalogo) {
      alert('Somente lojistas e administradores podem usar o catálogo.');
      return;
    }

    if (!podeUsarCatalogo) {
      alert('O catálogo está disponível apenas para lojistas Prata ou superior.');
      return;
    }
    
    if (!novoProdutoPadrao.nome_produto || !novoProdutoPadrao.id_tag) {
      alert("Preencha o nome e a categoria!");
      return;
    }
    
    if (!novoProdutoPadrao.id && !novoProdutoPadrao.imagem) {
      alert("Anexe uma imagem para o produto!");
      return;
    }

    setIsSalvandoProduto(true);
    const storage = getStorage();
    
    const tagSelecionada = tags.find(t => t.id === novoProdutoPadrao.id_tag);
    const nomeTagReal = tagSelecionada ? tagSelecionada.nome_tag : "";
    const urlImagemAnterior = novoProdutoPadrao.url_imagem || "";
    let urlFinal = urlImagemAnterior;
    let imagemEnviadaRef = null;
    let produtoSalvo = false;

    try {
      if (novoProdutoPadrao.imagem) {
        const opcoesCompressao = { maxSizeMB: 0.1, maxWidthOrHeight: 800, useWebWorker: true, fileType: 'image/webp' };
        const imagemComprimida = await imageCompression(novoProdutoPadrao.imagem, opcoesCompressao);
        const imgRef = ref(storage, `catalogo_padrao/${usuarioAtual.id || auth.currentUser.uid}_${Date.now()}.webp`);
        await uploadBytes(imgRef, imagemComprimida, {
          contentType: imagemComprimida?.type || 'image/webp',
        });
        imagemEnviadaRef = imgRef;
        urlFinal = await getDownloadURL(imgRef);
      }

      if (!urlFinal && !novoProdutoPadrao.imagem) {
         throw new Error("A foto do produto desapareceu da memória. Por favor, anexe a foto novamente.");
      }

      if (novoProdutoPadrao.id) {
        // xa ATUALIZAR NO BANCO DE DADOS
        const docRef = doc(db, "produtos_padrao", novoProdutoPadrao.id);
        await updateDoc(docRef, {
          nome_produto: novoProdutoPadrao.nome_produto,
          nome_busca: novoProdutoPadrao.nome_produto.toLowerCase(),
          id_tag: novoProdutoPadrao.id_tag,
          nome_tag: nomeTagReal,
          url_imagem: urlFinal, 
          timestamp_atualizacao: serverTimestamp()
        });
        produtoSalvo = true;

        if (imagemEnviadaRef && urlImagemAnterior && urlImagemAnterior !== urlFinal) {
          try {
            await deleteObject(ref(storage, urlImagemAnterior));
          } catch (storageError) {
            console.warn('A foto anterior do catálogo não pôde ser removida:', storageError);
          }
        }
        
        await buscarProdutosDoLojista({ ...filtroCatalogo, forcar: true });
        
        alert("Produto atualizado com sucesso!");
      } else {
        // xa CRIAR NO BANCO DE DADOS
        const idDonoDoCatalogo = usuarioAtual.id || auth.currentUser.uid;
        
        const novoDoc = {
          id_lojista: idDonoDoCatalogo,
          nome_produto: novoProdutoPadrao.nome_produto,
          nome_busca: novoProdutoPadrao.nome_produto.toLowerCase(),
          id_tag: novoProdutoPadrao.id_tag,
          nome_tag: nomeTagReal,
          url_imagem: urlFinal,
          data_criacao: serverTimestamp()
        };
        
        await addDoc(collection(db, "produtos_padrao"), novoDoc);
        produtoSalvo = true;
        
        await buscarProdutosDoLojista({ ...filtroCatalogo, forcar: true });
        
        alert("Produto salvo no catálogo com sucesso!");
      }

      cancelarEdicao();
    } catch (error) {
      if (!produtoSalvo && imagemEnviadaRef) {
        try {
          await deleteObject(imagemEnviadaRef);
        } catch (storageError) {
          console.warn('A imagem temporária do catálogo não pôde ser removida:', storageError);
        }
      }
      alert("Erro ao salvar: " + error.message);
    } finally {
      setIsSalvandoProduto(false);
    }
  };

const excluirProdutoPadrao = async (id) => {
    if (!podeOperarCatalogo || !podeUsarCatalogo) {
      alert('O catálogo está disponível apenas para lojistas Prata ou superior.');
      return;
    }

    if (window.confirm("Remover este produto do seu catálogo padrão?")) {
      try {
        const produtoParaApagar = produtosPadrao.find(p => p.id === id);

        await deleteDoc(doc(db, "produtos_padrao", id));

        if (produtoParaApagar && produtoParaApagar.url_imagem && produtoParaApagar.url_imagem.includes('firebasestorage')) {
          const storage = getStorage();
          const fotoRef = ref(storage, produtoParaApagar.url_imagem);
          try {
            await deleteObject(fotoRef);
            console.log("Foto do catálogo excluída do Storage.");
          } catch (storageError) {
            console.error("Aviso: Foto não encontrada ou já excluída no Storage:", storageError);
          }
        }

        await buscarProdutosDoLojista({ ...filtroCatalogo, forcar: true });
      } catch (error) { 
        alert("Erro ao excluir: " + error.message); 
      }
    }
  };

  const adicionarLinhaLote = () => { setLoteProdutos([...loteProdutos, { id_temp: Date.now(), nome_produto: '', preco: '', id_tag: '', imagem: null, data_validade: '', usando_catalogo: false, url_imagem_padrao: null }]); };
  const removerLinhaLote = (id) => setLoteProdutos(loteProdutos.filter(p => p.id_temp !== id));
  const handleLoteChange = (id, campo, valor) => { setLoteProdutos(prev => prev.map(p => p.id_temp === id ? { ...p, [campo]: valor } : p)); };
  const handleLoteMultiChange = (id, atualizacoes) => { setLoteProdutos(prev => prev.map(p => p.id_temp === id ? { ...p, ...atualizacoes } : p)); };

  const enviarLoteAoFirebase = async () => {
    const isAdmin = usuarioAtual?.perfis_ativos?.includes('admin');
    const operadorInterno = isAdmin || isTechnical;

    if (!podeOperarPublicacao) {
      alert('Somente lojistas e administradores podem publicar ofertas.');
      return;
    }

    const qtdNoLote = loteProdutos.length;
    const localSelecionadoNoLote = operadorInterno
      ? locaisDoLote.find((local) => local.id === localSelecionadoNoLoteId)
      : null;

    // ... [As verificações de cotas dos planos gratuitas/bronze continuam as mesmas aqui] ...
    if (!operadorInterno && planoAtual !== 'ouro' && planoAtual !== 'prata' && planoAtual !== 'diamante') {
      if (planoAtual === 'bronze') {
        const limiteSemana = configGerais.limite_bronze || 20;
        if (postagensWebNaSemana + qtdNoLote > limiteSemana) { alert('Bloqueado! O limite de postagens em lote do seu plano foi atingido.'); return; }
      }
      if (planoAtual === 'gratuito' || planoAtual === 'usuario') {
        const limiteSemana = configGerais.limite_gratuito || 5; 
        const limiteMes = configGerais.limite_mensal_gratuito || 300;
        if (postagensWebNaSemana + qtdNoLote > limiteSemana) { alert('Bloqueado! O limite semanal de postagens em lote do seu plano foi atingido.'); return; }
        if (postagensMensaisGeral + qtdNoLote > limiteMes) { alert('Bloqueado! O limite mensal de postagens em lote do seu plano foi atingido.'); return; }
      }
    }

    if (!usuarioAtual.latitude || !usuarioAtual.longitude) {
      alert("Erro: GPS não configurado. Salve a sua localização no Perfil da App primeiro!");
      return;
    }

    if (operadorInterno && (!localSelecionadoNoLote?.nome || !localSelecionadoNoLote?.cidade || !localSelecionadoNoLote?.estado)) {
      alert('Selecione uma loja cadastrada com cidade e UF antes de publicar o lote.');
      return;
    }

    // Pré-validação: publica o lote inteiro ou não publica nada.
    for (let p of loteProdutos) {
      if (!p.nome_produto || !p.preco || !p.id_tag) {
        alert(`Faltam dados na oferta: "${p.nome_produto || 'Sem nome'}". Preencha nome, categoria e preço.`);
        return;
      }
      if (!p.usando_catalogo && !p.imagem) {
        alert(`Faltou anexar a fotografia do produto: "${p.nome_produto}"`);
        return;
      }
    }

    setIsEnviandoLote(true);
    setProgressoEnvio('A iniciar o carregamento...');
    const storage = getStorage();
    const diasLimpeza = Number(configGerais.dias_limpeza_postagens) || 3;

    try {
      // 2. Upload das fotografias com progresso (processa as imagens antes do banco de dados)
      const urlsFinais = [];
      for (let i = 0; i < loteProdutos.length; i++) {
        let p = loteProdutos[i];
        setProgressoEnvio(`A enviar fotografias... (${i + 1} de ${loteProdutos.length})`);
        
        if (p.usando_catalogo && p.url_imagem_padrao && podeUsarCatalogo) {
          urlsFinais.push(p.url_imagem_padrao);
        } else {
          const opcoesCompressao = { maxSizeMB: 0.1, maxWidthOrHeight: 800, useWebWorker: true, fileType: 'image/webp' };
          const imagemComprimida = await imageCompression(p.imagem, opcoesCompressao);
          const refI = ref(storage, `promocoes_fotos/${auth.currentUser.uid}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.webp`);
          await uploadBytes(refI, imagemComprimida, {
            contentType: imagemComprimida?.type || 'image/webp',
          });
          urlsFinais.push(await getDownloadURL(refI));
        }
      }

      // 3. Escrita em lote (WriteBatch): grava as 30 em um ?nico disparo blindado!
      setProgressoEnvio('A processar no banco de dados...');
      const batch = writeBatch(db);
      const categoriasResumo = {};
      const categoriasNotificadas = new Set();
      const filiaisSet = new Set();
      const loteVitrineId = `vitrine_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const lojasComReconstrucaoVitrine = new Set();
      const autorId = usuarioAtual.id || usuarioAtual.uid || auth.currentUser?.uid;
      const destinoAdministrativo = operadorInterno
        ? {
            id: autorId,
            nome: usuarioAtual.nome || 'PromoAlert',
            url_foto_perfil: usuarioAtual.url_foto_perfil || null,
            cidade: localSelecionadoNoLote.cidade,
            estado: localSelecionadoNoLote.estado,
            local: localSelecionadoNoLote.nome,
          }
        : null;

      for (let i = 0; i < loteProdutos.length; i++) {
        let p = loteProdutos[i];
        let url = urlsFinais[i];
        const raioPermitido = (planoAtual === 'ouro' || planoAtual === 'diamante') ? (usuarioAtual.raio_alcance_km || 50) : 0;
        const lojasDestino = destinoAdministrativo
          ? [destinoAdministrativo]
          : (isFilial ? [usuarioAtual] : [usuarioAtual, ...(p.lojas_selecionadas || [])]).filter((loja, index, lojas) => {
              const lojaId = String(loja?.id || loja?.uid || '').trim();
              return lojaId && lojas.findIndex((item) => String(item?.id || item?.uid || '').trim() === lojaId) === index;
            });
        const idOfertaMestra = `mestra_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const tagEncontrada = tags.find(t => t.id === p.id_tag);
        const nomeDaCategoria = tagEncontrada ? tagEncontrada.nome_tag : 'Ofertas';
        const representantePushDaCategoria = operadorInterno && !categoriasNotificadas.has(p.id_tag);
        categoriasNotificadas.add(p.id_tag);
        categoriasResumo[nomeDaCategoria] = (categoriasResumo[nomeDaCategoria] || 0) + 1;

        if (!destinoAdministrativo) {
          lojasDestino.forEach(loja => {
            const lojaId = String(loja?.id || loja?.uid || '').trim();
            if (lojaId && lojaId !== autorId) filiaisSet.add(lojaId);
          });
        }

        const idsLojasDestino = lojasDestino
          .map((loja) => String(loja.id || loja.uid || '').trim())
          .filter(Boolean);

        for (let lojaDestino of lojasDestino) {
          const lojaDestinoId = String(lojaDestino.id || lojaDestino.uid || '').trim();
          const deveReconstruirVitrine = !operadorInterno && lojaDestinoId && !lojasComReconstrucaoVitrine.has(lojaDestinoId);
          if (deveReconstruirVitrine) {
            lojasComReconstrucaoVitrine.add(lojaDestinoId);
          }
          let dataExclusao = new Date();
          dataExclusao.setDate(dataExclusao.getDate() + diasLimpeza);
          let dataValidadeAjustada = null;
          if (p.data_validade) dataValidadeAjustada = new Date(p.data_validade + 'T23:59:59');
          let limiteExibicao = dataValidadeAjustada ? dataValidadeAjustada : dataExclusao;
          const isPremiumFuraBolha = (planoAtual === 'ouro' || planoAtual === 'diamante');

          const cid = lojaDestino.cidade_presenca || lojaDestino.cidade || usuarioAtual.cidade_presenca || usuarioAtual.cidade;
          const est = lojaDestino.estado_presenca || lojaDestino.estado || usuarioAtual.estado_presenca || usuarioAtual.estado;
          let topicoLocal = '';
          if (cid && est) {
              const cFormatada = cid.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "");
              const eFormatado = est.toLowerCase().trim();
              topicoLocal = `cidade_${cFormatada}_${eFormatado}`;
          }

          // Usa o Batch para enfileirar as ações
          const docRef = doc(collection(db, "promocoes")); 
          batch.set(docRef, {
            id_usuario: lojaDestinoId,
            nome_usuario: operadorInterno ? (usuarioAtual.nome || 'PromoAlert') : lojaDestino.nome,
            url_foto_usuario: lojaDestino.url_foto_perfil || null,
            perfil_usuario: operadorInterno ? 'usuario' : 'lojista',
            plano: operadorInterno ? 'gratuito' : planoAtual,
            latitude: lojaDestino.latitude || usuarioAtual.latitude,
            longitude: lojaDestino.longitude || usuarioAtual.longitude,
            raio_alcance_km: operadorInterno ? 0 : raioPermitido,
            local: operadorInterno ? lojaDestino.local : lojaDestino.nome,
            cidade_oferta: cid || '',
            estado_oferta: est || '',
            nome_produto: p.nome_produto,
            nome_busca: p.nome_produto.toLowerCase(),
            preco: parseFloat(p.preco),
            id_tag: p.id_tag,
            nome_tag: tags.find(t => t.id === p.id_tag)?.nome_tag || '',
            url_foto: url,
            data_validade: dataValidadeAjustada, 
            data_exclusao: dataExclusao, 
            data_limite_exibicao: limiteExibicao, 
            fura_bolha_ativo: operadorInterno ? false : isPremiumFuraBolha, 
            id_oferta_mestra: idOfertaMestra,
            qtd_lojas_destino: idsLojasDestino.length || 1,
            qtd_outras_unidades: idsLojasDestino.length > 1 ? idsLojasDestino.length - 1 : 0,
            lojas_destino_ids: idsLojasDestino.length ? idsLojasDestino : [String(lojaDestino.id || lojaDestino.uid || '').trim()],
            origem: 'web',
            likes: [],
            qtd_comentarios: 0,
            timestamp: serverTimestamp(),
            // Controle de push independente da classificacao de matriz/filial.
            push_lote_id: loteVitrineId,
            push_lote_representante: representantePushDaCategoria,
        // Uma filial pode publicar varios itens para si. A postagem so e de rede
        // quando ha mais de uma loja de destino.
        postagem_em_lote: !operadorInterno && idsLojasDestino.length > 1,
            ...(operadorInterno ? {} : {
              lote_vitrine_id: loteVitrineId,
              reconciliar_vitrine_lote: deveReconstruirVitrine,
            }),
            topico_origem: topicoLocal, 
            topicos_push: topicoLocal ? [topicoLocal] : []
          });
        }
      }

      // Executa tudo de uma vez (Se a net cair antes daqui, nenhuma oferta é guardada, evitando poluição!)
      await batch.commit();
      setProgressoEnvio('Processando postagens...');

      // O cadastro oficial de locais agora é administrativo. A publicação comercial
      // não deve mais tentar criar/atualizar essa base durante o lote.

      if (!operadorInterno) {
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const dispararAlerta = httpsCallable(functions, 'notificarGeograficamenteLote');
        let cidadesAlvoRaio = [];
        const raioPermitido = (planoAtual === 'ouro' || planoAtual === 'diamante') ? (usuarioAtual.raio_alcance_km || 50) : 0;
        
        if (raioPermitido > 0 && usuarioAtual.latitude && usuarioAtual.longitude) {
           try {
              const respostaJson = await fetch('/municipios.json');
              const todasCidades = await respostaJson.json();
              todasCidades.forEach(cid => {
                 const distancia = calcularDistanciaKM(usuarioAtual.latitude, usuarioAtual.longitude, cid.latitude, cid.longitude);
                 if (distancia <= raioPermitido && distancia > 1) {
                    cidadesAlvoRaio.push({ cidade: cid.nome, estado: cid.codigo_uf === 42 ? 'SC' : 'BR' });
                 }
              });
           } catch (erroJson) {}
        }

        await dispararAlerta({ 
          lojaId: usuarioAtual.uid || usuarioAtual.id, 
          categorias: categoriasResumo,
          filiaisSelecionadas: Array.from(filiaisSet),
          cidadesVizinhas: cidadesAlvoRaio,
        });
      } catch (err) {}
      }

      // Cobrança de hodômetro.
      if (!operadorInterno && planoAtual !== 'ouro' && planoAtual !== 'prata' && planoAtual !== 'diamante') {
        const agora = new Date();
        const mesAtual = `${String(agora.getMonth() + 1).padStart(2, '0')}-${agora.getFullYear()}`;
        const domingo = new Date(agora);
        domingo.setDate(agora.getDate() - agora.getDay());
        const semanaAtual = `${String(domingo.getDate()).padStart(2, '0')}-${String(domingo.getMonth() + 1).padStart(2, '0')}-${domingo.getFullYear()}`;
        
        try {
          const userRef = doc(db, "users", usuarioAtual.id || auth.currentUser.uid);
          await updateDoc(userRef, {
            postagens_gastas: postagensMensaisGeral + loteProdutos.length,
            mes_referencia: mesAtual,
            postagens_gastas_semana: postagensWebNaSemana + loteProdutos.length,
            semana_referencia: semanaAtual
          });
        } catch (err) {}
      }

      setProgressoEnvio('');
      alert("Sucesso! Todas as ofertas foram publicadas.");
      setLoteProdutos([]);

    } catch (e) {
      alert("Erro ao publicar o lote: " + e.message);
    } finally {
      setIsEnviandoLote(false);
      setProgressoEnvio('');
    }
  };

  const handleBotaoAssinar = () => alert("Módulo de pagamento em fase final de configuração.");

  const possuiReautenticacaoInternaRecente = useCallback(
    () => (Date.now() - ultimaReautenticacaoInternaAt) < REAUTH_ADMIN_WINDOW_MS,
    [ultimaReautenticacaoInternaAt],
  );

  const executarAcaoInternaProtegida = useCallback(async (acao) => {
    if (possuiReautenticacaoInternaRecente()) {
      await acao();
      return;
    }

    setAcaoInternaPendente(() => acao);
    setModalReautenticacaoInternaAberto(true);
  }, [possuiReautenticacaoInternaRecente]);

  const concluirReautenticacaoInterna = useCallback(async () => {
    const acao = acaoInternaPendente;
    setModalReautenticacaoInternaAberto(false);
    setAcaoInternaPendente(null);
    setUltimaReautenticacaoInternaAt(Date.now());

    if (acao) {
      await acao();
    }
  }, [acaoInternaPendente]);

  const cancelarReautenticacaoInterna = useCallback(() => {
    rejeitarLimpezaPendente.current?.(new Error('Confirmação de identidade cancelada. Nenhuma nova etapa foi iniciada.'));
    rejeitarLimpezaPendente.current = null;
    setModalReautenticacaoInternaAberto(false);
    setAcaoInternaPendente(null);
  }, []);

  const executarGerenciamentoUsuarioPainel = useCallback(async (payload) => {
    const resposta = await postJsonWithAuth(montarUrlFuncaoHttp('gerenciarUsuariosPainel'), payload);
    const dadosRetorno = await resposta.json().catch(() => ({}));

    if (!resposta.ok || !dadosRetorno?.sucesso) {
      throw new Error(dadosRetorno?.erro || 'Não foi possível concluir a operação de usuários agora.');
    }

    return dadosRetorno;
  }, []);

  const sincronizarUsuarioEmMemoria = useCallback((usuarioAtualizado) => {
    if (!usuarioAtualizado?.id) {
      return;
    }

    setUsuarios((prev) => prev.map((u) => (u.id === usuarioAtualizado.id ? { ...u, ...usuarioAtualizado } : u)));
  }, []);

  const aprovarPerfil = async (user, perfilDesejado) => {
    if (window.confirm(`Aprovar ${perfilDesejado.toUpperCase()} para ${user.nome}?`)) {
      await executarAcaoInternaProtegida(async () => {
        try {
          const dadosRetorno = await executarGerenciamentoUsuarioPainel({
            action: 'approve_request',
            userId: user.id,
            profile: perfilDesejado,
          });
          sincronizarUsuarioEmMemoria(dadosRetorno.user);
          return dadosRetorno.user;
        } catch (e) {
          console.error(e);
          alert(e.message || 'Não foi possível aprovar a solicitação agora.');
        }
      });
    }
  };

  const recusarPerfil = async (user, perfilRecusado) => { 
    if (window.confirm(`Recusar pedido de ${perfilRecusado.toUpperCase()} para ${user.nome}?`)) { 
      await executarAcaoInternaProtegida(async () => {
        try { 
          const dadosRetorno = await executarGerenciamentoUsuarioPainel({
            action: 'reject_request',
            userId: user.id,
            profile: perfilRecusado,
          });
          sincronizarUsuarioEmMemoria(dadosRetorno.user);
          return dadosRetorno.user;
        } catch (e) { 
          console.error(e); 
          alert(e.message || 'Não foi possível recusar a solicitação agora.');
        } 
      });
    } 
  };

  const alternarStatusUsuario = async (user) => { 
    const estaAtivo = user.is_ativo !== false; 
    if (window.confirm(`Deseja ${estaAtivo ? 'BLOQUEAR' : 'REATIVAR'} ${user.nome}?`)) { 
      await executarAcaoInternaProtegida(async () => {
        try { 
          const dadosRetorno = await executarGerenciamentoUsuarioPainel({
            action: 'toggle_active',
            userId: user.id,
          });
          sincronizarUsuarioEmMemoria(dadosRetorno.user);
          return dadosRetorno.user;
        } catch (e) { 
          console.error(e); 
          alert(e.message || 'Não foi possível atualizar o status deste usuário agora.');
        } 
      });
    } 
  };

  const removerPerfilAtivo = async (user, perfil) => { 
    if (window.confirm(`Remover perfil ${perfil.toUpperCase()} de ${user.nome}?`)) { 
      await executarAcaoInternaProtegida(async () => {
        try { 
          const dadosRetorno = await executarGerenciamentoUsuarioPainel({
            action: 'remove_profile',
            userId: user.id,
            profile: perfil,
          });
          sincronizarUsuarioEmMemoria(dadosRetorno.user);
          return dadosRetorno.user;
        } catch (e) { 
          console.error(e); 
          alert(e.message || 'Não foi possível remover este perfil agora.');
        } 
      });
    } 
  };

  const adicionarPerfilManual = async (user, novo) => { 
    if (!novo || (user.perfis_ativos || []).includes(novo)) return; 
    if (window.confirm(`Conceder acesso de ${novo.toUpperCase()} para ${user.nome}?`)) { 
      await executarAcaoInternaProtegida(async () => {
        try { 
          const dadosRetorno = await executarGerenciamentoUsuarioPainel({
            action: 'add_profile',
            userId: user.id,
            profile: novo,
          });
          sincronizarUsuarioEmMemoria(dadosRetorno.user);
          return dadosRetorno.user;
        } catch (e) { 
          console.error(e); 
          alert(e.message || 'Não foi possível conceder este perfil agora.');
        } 
      });
    } 
  };

  const alterarPlanoLojista = async (user, novoPlano, gerarCobranca = false, diasCarencia = 0, isencaoProRata = false, isUpgrade = false) => { 
    await executarAcaoInternaProtegida(async () => {
      try { 
        const { usuarioAtualizado, financeiro } = await processarAlteracaoPlanoAdministrativo({
          usuario: user, novoPlano, gerarCobranca, diasCarencia, isencaoProRata, isUpgrade,
        }, executarGerenciamentoUsuarioPainel);
        sincronizarUsuarioEmMemoria(usuarioAtualizado);

        if (financeiro) {
          if (!financeiro.isento && financeiro.linkPagamento) {
            setFaturaPlanoParaAbrir(financeiro.linkPagamento);
          } else {
            alert('Plano atualizado com sucesso.');
          }
        }

        return usuarioAtualizado;

      } catch (e) { 
        console.error(e); 
        alert(e.message || "Erro crítico ao processar alteração de plano.");
      } 
    });
  };

  // Busca apenas postagens com a flag de denúncia.
  const buscarPromocoes = async (nova = false) => { 
    if (carregamentoModeracaoEmAndamentoRef.current) return;

    carregamentoModeracaoEmAndamentoRef.current = true;
    try { 
      if (nova) {
        ultimoDocumentoModeracaoRef.current = null;
      }

      const q = [collection(db, "promocoes")]; 
      q.push(where("denunciada", "==", true)); 

      if (dataInicio && dataFim) { 
        q.push(where("timestamp", ">=", new Date(`${dataInicio}T00:00:00`)), where("timestamp", "<=", new Date(`${dataFim}T23:59:59`))); 
      } 
      q.push(orderBy("timestamp", "desc"), limit(10)); 

      if (!nova && ultimoDocumentoModeracaoRef.current) {
        q.push(startAfter(ultimoDocumentoModeracaoRef.current));
      }

      const res = await getDocs(query(...q)); 

      if (res.empty) { 
        setTemMaisDados(false); 
        if (nova) setPromocoes([]); 
        return; 
      }

      ultimoDocumentoModeracaoRef.current = res.docs[res.docs.length - 1];
      setTemMaisDados(res.docs.length === 10);
      const lista = res.docs.map(d => ({ id: d.id, ...d.data() })); 

      if (nova) {
        setPromocoes(lista);
      } else {
        // Defesa adicional: mesmo com clique repetido, um documento nunca aparece duas vezes.
        setPromocoes(prev => {
          const idsExistentes = new Set(prev.map(item => item.id));
          return [...prev, ...lista.filter(item => !idsExistentes.has(item.id))];
        });
      }
    } catch (e) { 
      console.error(e); 
      alert("Erro ao buscar denúncias. Verifique o console se o Firebase pediu para criar um Índice.");
    } finally {
      carregamentoModeracaoEmAndamentoRef.current = false;
    } 
  };

  const excluirPromocao = async (id, urlFoto) => {
     if (window.confirm("Tem certeza que deseja excluir esta postagem?")) {
       try {
         const comentariosSnap = await getDocs(collection(db, "promocoes", id, "comentarios"));
         const promessasExclusao = comentariosSnap.docs.map(comentarioDoc => deleteDoc(comentarioDoc.ref));
         await Promise.all(promessasExclusao);
         
         await deleteDoc(doc(db, "promocoes", id));
         
         setPromocaoSelecionada(null);
         buscarPromocoes(true);
       } catch (e) {
         console.error("Erro ao excluir postagem:", e);
         alert("Erro ao excluir a postagem.");
       }
     }
   };

  // Encerra a denúncia e remove os registros individuais da postagem.
  const aprovarDenuncia = async (id) => {
    if (window.confirm("Aprovar esta postagem e limpar o alerta de denúncia?")) {
      try {
        const denunciasRef = collection(db, "promocoes", id, "denuncias");
        const denunciasSnapshot = await getDocs(denunciasRef);
        const lote = writeBatch(db);

        lote.update(doc(db, "promocoes", id), {
          denunciada: false,
          qtd_denuncias: 0
        });
        denunciasSnapshot.docs.forEach((denunciaDoc) => lote.delete(denunciaDoc.ref));
        await lote.commit();

        setPromocaoSelecionada(null);
        buscarPromocoes(true); // Atualiza a lista (o item vai sumir da fila!)
        alert("Postagem aprovada e alerta removido!");
      } catch (e) {
        console.error(e);
        alert("Erro ao aprovar.");
      }
    }
  };
  const adicionarTag = async (e) => {
    e.preventDefault();
    const nomeNormalizado = normalizarNomeTag(novaTag);
    if (!nomeNormalizado.trim()) return;

    try {
      const docRef = await addDoc(collection(db, "tags"), { nome_tag: nomeNormalizado });
      setTags((prev) => ordenarTagsAlfabeticamente([
        ...prev,
        { id: docRef.id, nome_tag: nomeNormalizado },
      ]));
      setNovaTag('');
    } catch (e) {
      console.error(e);
      alert("Não foi possível adicionar a tag agora.");
    }
  };
  const excluirTag = async (id) => {
    if (!window.confirm("Apagar tag?")) return;
    try {
      await deleteDoc(doc(db, "tags", id));
      setTags((prev) => prev.filter((tag) => tag.id !== id));
      if (editandoId === id) {
        setEditandoId(null);
        setTextoEditado('');
      }
    } catch (e) {
      console.error(e);
      alert("Não foi possível excluir a tag agora.");
    }
  };
  const iniciarEdicao = (tag) => { setEditandoId(tag.id); setTextoEditado(tag.nome_tag); };
  const salvarEdicao = async (id) => {
    const nomeNormalizado = normalizarNomeTag(textoEditado);
    if (!nomeNormalizado.trim()) return;
    try {
      await updateDoc(doc(db, "tags", id), { nome_tag: nomeNormalizado });
      setTags((prev) => ordenarTagsAlfabeticamente(
        prev.map((tag) => (tag.id === id ? { ...tag, nome_tag: nomeNormalizado } : tag)),
      ));
      setTextoEditado(nomeNormalizado);
      setEditandoId(null);
    } catch (e) {
      console.error(e);
      alert("Não foi possível salvar a tag agora.");
    }
  };
  const fazerLogout = () => signOut(auth);
  const handleConfigChange = async (c, v) => {
    try {
      if (ADMIN_MFA_REQUIRED && c === 'mfa_admin_obrigatorio') {
        setConfigGerais(prev => ({ ...prev, mfa_admin_obrigatorio: true }));
        return;
      }

      await setDoc(doc(db, "configuracoes", "gerais"), { [c]: v }, { merge: true });

      setConfigGerais(prev => ({ ...prev, [c]: v }));
    } catch (e) {
      console.error(e);
    }
  };
  const handleCheckboxLimpeza = (campo) => setOpcoesLimpeza(prev => ({ ...prev, [campo]: !prev[campo] }));

  const usuariosPendentes = usuarios.filter(user => user.status_aprovacao === 'pendente' && user.perfis_solicitados?.length > 0);
  const limMensalGratuitoVisual = configGerais.limite_mensal_gratuito || 300;
  const filiaisPendentesDaFila = Array.isArray(usuarioAtual?.filiais_pendentes_vinculo)
    ? usuarioAtual.filiais_pendentes_vinculo
    : [];
  const filiaisPendentesLegadas = minhasFiliais.filter((filial) => filial.status_vinculo === 'pendente_pagamento');
  const filiaisPendentes = [...filiaisPendentesDaFila, ...filiaisPendentesLegadas]
    .filter((filial, index, lista) => lista.findIndex((item) => item.id === filial.id) === index);
  const filiaisAtivas = minhasFiliais.filter((filial) => filial.status_vinculo !== 'pendente_pagamento');

  const papelUsuario = isAdmin
    ? 'Administrador'
    : isTechnical
      ? 'Técnico'
    : isModerator
      ? 'Moderador'
    : isFilial
      ? 'Filial'
      : 'Loja Matriz';

  const resumoStatus = estaInadimplente
    ? { valor: 'Acesso bloqueado', apoio: 'Regularize o financeiro para liberar o painel.', classe: 'is-danger' }
    : estaEmCarencia
      ? { valor: 'Em carência', apoio: 'Ainda há acesso, mas existe pagamento pendente.', classe: 'is-warning' }
      : { valor: 'Operacional', apoio: 'Painel liberado para uso normal.', classe: 'is-success' };

  const planoAtualRotulo = !isAdmin && !isLojista && isModerator
    ? 'MODERAÇÃO'
    : String(planoAtual || 'gratuito').toUpperCase();
  const resumoTerceiro = isAdmin
    ? {
        rotulo: 'Fila administrativa',
        valor: pendenciasCarregadas
          ? `${usuariosPendentes.length} pendência${usuariosPendentes.length === 1 ? '' : 's'}`
          : 'Consultar fila',
        apoio: pendenciasCarregadas
          ? 'Aprovações aguardando revisão no painel.'
          : 'Abra Aprovações para carregar a fila sem consulta automática no login.',
      }
    : isModerator
      ? {
          rotulo: 'Fila de moderação',
          valor: 'Acesso restrito',
          apoio: 'Este perfil atua somente na revisão de conteúdo denunciado.',
        }
    : isFilial
      ? {
          rotulo: 'Vínculo da conta',
          valor: 'Unidade vinculada',
          apoio: 'A gestão comercial é controlada pela loja matriz.',
        }
      : {
          rotulo: 'Rede ativa',
          valor: `${filiaisAtivas.length} unidade${filiaisAtivas.length === 1 ? '' : 's'}`,
          apoio: filiaisPendentes.length > 0
            ? `${filiaisPendentes.length} filial${filiaisPendentes.length === 1 ? '' : 'is'} aguardando pagamento.`
            : 'Sem ativações pendentes no momento.',
        };

  const planoAtualRotuloExibicao = isTechnical && !isAdmin && !isLojista
    ? 'OPERAÇÃO TÉCNICA'
    : isModerator && !isAdmin && !isLojista
      ? 'MODERAÇÃO'
      : planoAtualRotulo;
  const resumoTerceiroExibicao = isTechnical && !isAdmin
    ? {
        rotulo: 'Modo de acesso',
        valor: 'Operação e aprovações',
        apoio: 'Este perfil opera rotinas administrativas permitidas, publica no painel e também pode liberar acessos pendentes.',
      }
    : resumoTerceiro;
  const rotuloCentralPainel = isAdmin
    ? 'Central Operacional'
    : isTechnical
      ? 'Central Técnica'
      : 'Central do Lojista';
  const rotuloSecaoPainel = isAdmin || isTechnical
    ? 'Gestão interna'
    : 'Operação diária';
  const apoioPlanoPainel = isTechnical
    ? 'Acesso interno para operação administrativa permitida e liberação de acessos.'
    : isFilial
      ? 'Gerenciado pela matriz.'
      : 'Estrutura comercial ativa no painel.';

  const definicoesAbas = {
    inicio: { titulo: 'Início', descricao: '' },
    postagemLote: { titulo: 'Postagem em lote', descricao: '' },
    meusProdutos: { titulo: 'Meu catálogo', descricao: '' },
    filiais: { titulo: 'Minhas filiais', descricao: '' },
    financeiro: { titulo: 'Financeiro', descricao: '' },
    locais: { titulo: 'Gestão de locais', descricao: '' },
    planos: { titulo: 'Minha assinatura', descricao: '' },
    relatorios: { titulo: 'Inteligência de mercado', descricao: '' },
    manutencao_lojas: { titulo: 'Padronização de lojas', descricao: '' },
    usuarios: { titulo: 'Usuários', descricao: '' },
    clientes: { titulo: 'Clientes', descricao: '' },
    aprovacoes: { titulo: 'Aprovações', descricao: '' },
    postagens: { titulo: 'Moderação', descricao: '' },
    tags: { titulo: 'Tags', descricao: '' },
    configuracoes: { titulo: 'Configurações', descricao: '' },
    afiliados: { titulo: 'Afiliados', descricao: '' },
    limpeza: { titulo: 'Limpeza', descricao: '' },
    bloqueado_filial: { titulo: 'Acesso suspenso', descricao: 'Esta unidade está temporariamente bloqueada por dependência financeira da matriz.' },
  };

  const abaAtualInfo = definicoesAbas[abaAtiva] || {
    titulo: isAdmin ? 'Painel administrativo' : 'Painel do lojista',
    descricao: 'Visual moderno com impacto zero em leituras, listeners e custo do Firebase.',
  };

  const secoesMenu = [];

  if (estaInadimplente) {
    if (!isFilial) {
      secoesMenu.push({
        titulo: 'Urgente',
        itens: [
          { id: 'financeiro', rotulo: 'Financeiro pendente', subtitulo: 'Regularize o acesso da conta', icone: 'warning', destaque: 'danger' },
        ],
      });
    }
  } else {
    secoesMenu.push({
      titulo: 'Visão geral',
      itens: [
        { id: 'inicio', rotulo: 'Início', subtitulo: 'Resumo do acesso', icone: 'home' },
      ],
    });

    const atalhosLojista = [];
    if (isLojista || isAdmin) {
      atalhosLojista.push({ id: 'postagemLote', rotulo: 'Postagem em lote', subtitulo: 'Publicações rápidas', icone: 'batch' });

      if (planoAtual !== 'gratuito' || configGerais.exibir_premium || isAdmin) {
        atalhosLojista.push({
          id: 'meusProdutos',
          rotulo: !podeUsarCatalogo && !isAdmin ? 'Catálogo premium' : 'Meu catálogo',
          subtitulo: !podeUsarCatalogo && !isAdmin ? 'Disponível a partir do Prata' : 'Produtos padrão',
          icone: 'catalog',
        });
      }

      if ((planoAtual === 'ouro' || planoAtual === 'diamante' || isAdmin) && !usuarioAtual?.id_usuario_pai) {
        atalhosLojista.push({
          id: 'filiais',
          rotulo: 'Minhas filiais',
          subtitulo: filiaisPendentes.length > 0 ? 'Existe ativação pendente' : 'Rede e expansão',
          icone: 'branches',
          badge: filiaisPendentes.length > 0 ? String(filiaisPendentes.length) : null,
        });
      }

      if (isAdmin || planoAtual !== 'gratuito') {
        atalhosLojista.push({ id: 'financeiro', rotulo: 'Financeiro', subtitulo: 'Cobrança e assinatura', icone: 'finance' });
      }

      if (isAdmin) {
        atalhosLojista.push({ id: 'locais', rotulo: 'Gestão de locais', subtitulo: 'Base operacional', icone: 'locations' });
      }

      if (configGerais.exibir_premium && !usuarioAtual?.id_usuario_pai) {
        atalhosLojista.push({ id: 'planos', rotulo: 'Minha assinatura', subtitulo: 'Planos e recursos', icone: 'plans' });
      }
    }

    if (atalhosLojista.length > 0) {
      secoesMenu.push({ titulo: isAdmin ? 'Operação' : 'Atalhos', itens: atalhosLojista });
    }

    const menuInsights = [];
    if (podeVerRelatorios) {
      menuInsights.push({ id: 'relatorios', rotulo: 'Inteligência de mercado', subtitulo: 'Demanda e região', icone: 'insights' });
    }
    if (podeVerManutencaoLojas) {
      menuInsights.push({ id: 'manutencao_lojas', rotulo: 'Padronização de lojas', subtitulo: 'Ajustes administrativos', icone: 'stores' });
    }
    if (menuInsights.length > 0) {
      secoesMenu.push({ titulo: 'Estratégia', itens: menuInsights });
    }

    if (isAdmin || isModerator || isTechnical) {
      if (isModerator && !isAdmin && !isTechnical) {
        secoesMenu.push({
          titulo: 'Moderação',
          itens: [
            { id: 'postagens', rotulo: 'Moderação', subtitulo: 'Conteúdo e denúncias', icone: 'moderation' },
          ],
        });
      } else {
        const itensAdministracao = [
          ...(isAdmin ? [{ id: 'clientes', rotulo: 'Clientes', subtitulo: 'Faturas e contratações', icone: 'finance' }] : []),
          { id: 'usuarios', rotulo: 'Usuários', subtitulo: 'Perfis e acesso', icone: 'users' },
          { id: 'aprovacoes', rotulo: 'Aprovações', subtitulo: 'Fila pendente', icone: 'approvals', badge: pendenciasCarregadas && usuariosPendentes.length > 0 ? String(usuariosPendentes.length) : null },
          { id: 'postagens', rotulo: 'Moderação', subtitulo: 'Conteúdo e denúncias', icone: 'moderation' },
          { id: 'tags', rotulo: 'Tags', subtitulo: 'Categorias do sistema', icone: 'tags' },
        ];

        if (podeVerConfiguracoes) {
          itensAdministracao.push({ id: 'configuracoes', rotulo: 'Configurações', subtitulo: 'Segurança e operação', icone: 'settings' });
        }

        if (podeVerAfiliados) {
          itensAdministracao.push({ id: 'afiliados', rotulo: 'Afiliados', subtitulo: 'Parcerias e regras', icone: 'affiliates' });
        }

        if (podeVerLimpeza) {
          itensAdministracao.push({ id: 'limpeza', rotulo: 'Limpeza', subtitulo: 'Manutenção manual', icone: 'cleanup' });
        }

        secoesMenu.push({
          titulo: 'Administração',
          itens: itensAdministracao,
        });
      }
    }

    if (isTechnical && !isAdmin) {
      secoesMenu.push({
        titulo: 'Operação técnica',
        itens: [
          { id: 'postagemLote', rotulo: 'Postagem em lote', subtitulo: 'Publicações operacionais', icone: 'batch' },
          { id: 'locais', rotulo: 'Locais', subtitulo: 'Base operacional', icone: 'locations' },
        ],
      });
    }
  }

  const renderMenuItem = (item) => {
    const classes = ['panel-nav-button'];
    if (abaAtiva === item.id) classes.push('is-active');
    if (item.destaque === 'danger') classes.push('is-danger');

    return (
      <button
        key={item.id}
        type="button"
        className={classes.join(' ')}
        onClick={() => setAbaAtiva(item.id)}
      >
        <span className="panel-nav-button__icon">
          <PanelIcon name={item.icone} />
        </span>
        <span className="panel-nav-button__text">
          <span className="panel-nav-button__label">{item.rotulo}</span>
        </span>
        {item.badge && <span className="panel-nav-button__badge">{item.badge}</span>}
      </button>
    );
  };

  if (!usuarioAtual) return <div className="panel-loading">Carregando sistema...</div>;

  return (
    <div className="panel-shell">
      <div className="panel-shell__inner">
        <header className="panel-header">
          <div className="panel-brand">
            <div className="panel-brand__mark">
              <PanelIcon name="brand" />
            </div>
            <div>
              <p className="panel-eyebrow">{rotuloCentralPainel}</p>
              <h1 className="panel-title">PromoAlert</h1>
              {abaAtualInfo.descricao && <p className="panel-subtitle">{abaAtualInfo.descricao}</p>}
            </div>
          </div>
          <div className="panel-userbox">
            <div className="panel-userbox__meta">
              <span className="panel-userbox__hello">Olá, <strong>{usuarioAtual.nome}</strong></span>
              <span className="panel-userbox__role">{papelUsuario} | Plano {planoAtualRotuloExibicao}</span>
            </div>
            <button type="button" className="panel-logout" onClick={fazerLogout}>Sair</button>
          </div>
        </header>

        <section className="panel-summary">
          <div className={`panel-summary-card ${resumoStatus.classe}`}>
            <span className="panel-summary-card__label">Status do acesso</span>
            <span className="panel-summary-card__value">{resumoStatus.valor}</span>
            <span className="panel-summary-card__helper">{resumoStatus.apoio}</span>
          </div>
          <div className="panel-summary-card">
            <span className="panel-summary-card__label">Plano atual</span>
            <span className="panel-summary-card__value">{planoAtualRotuloExibicao}</span>
            <span className="panel-summary-card__helper">{apoioPlanoPainel}</span>
          </div>
          <div className="panel-summary-card">
            <span className="panel-summary-card__label">{resumoTerceiroExibicao.rotulo}</span>
            <span className="panel-summary-card__value">{resumoTerceiroExibicao.valor}</span>
            <span className="panel-summary-card__helper">{resumoTerceiroExibicao.apoio}</span>
          </div>
        </section>

        <div className="panel-layout">
          <aside className="panel-sidebar">
              <div className="panel-sidebar__brand">
                <img
                  className="panel-sidebar__brand-logo"
                  src="/promoalert-panel-brand.png"
                  alt="PromoAlert"
                />
              </div>
              {secoesMenu.length === 0 ? (
                <div className="panel-sidebar__section">
                  <p className="panel-sidebar__title">Navegação</p>
                  <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6' }}>
                    O menu desta conta fica reduzido enquanto a situação financeira estiver bloqueada.
                  </div>
                </div>
              ) : (
                secoesMenu.map((secao) => (
                  <div key={secao.titulo} className="panel-sidebar__section">
                    <p className="panel-sidebar__title">{secao.titulo}</p>
                    <div className="panel-nav-list">
                      {secao.itens.map(renderMenuItem)}
                    </div>
                  </div>
                ))
              )}
          </aside>

          <main className="panel-content">
            {estaEmCarencia && !isFilial && (
              <div style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '15px', marginBottom: '18px', borderRadius: '14px', borderLeft: '5px solid #ffe08a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div>
                  <strong style={{ fontSize: '16px' }}>Atenção: sua fatura está em atraso</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
                    Seu acesso está em período de carência. Verifique o e-mail ou SMS para acessar o link de pagamento enviado pelo sistema de cobrança.
                  </p>
                </div>
                {usuarioAtual?.link_fatura_pendente ? (
                  <a
                    href={usuarioAtual.link_fatura_pendente}
                    target="_blank"
                    rel="noreferrer"
                    style={{ backgroundColor: '#ffc107', color: '#000', textDecoration: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: 'bold', textAlign: 'center', whiteSpace: 'nowrap' }}
                  >
                    Pagar fatura
                  </a>
                ) : (
                  <button onClick={() => setAbaAtiva('financeiro')} style={{ backgroundColor: '#ffc107', color: '#000', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' }}>
                    Ir para o financeiro
                  </button>
                )}
              </div>
            )}

            <section ref={panelContentSectionRef} className={`panel-content__section ${abaAtiva === 'inicio' ? 'panel-content__section--inicio' : ''}`}>
              {abaAtiva !== 'inicio' && (
                <div className="panel-content__header">
                  <div>
                    {abaAtualInfo.descricao && (
                      <p className="panel-content__eyebrow">{isAdmin ? 'Gestão interna' : 'Operação diária'}</p>
                    )}
                    <h2 className="panel-content__title">{abaAtualInfo.titulo}</h2>
                    {abaAtualInfo.descricao && (
                      <p className="panel-content__description">{abaAtualInfo.descricao}</p>
                    )}
                  </div>
                  <div className="routine-actions">
                    {abaAtiva === 'meusProdutos' && <BotaoAjuda tela="catalogo" />}
                    {abaAtiva === 'filiais' && <BotaoAjuda tela="filiais" />}
                    {abaAtiva === 'financeiro' && <BotaoAjuda tela="financeiro" />}
                    <div className="panel-content__pill">
                      <PanelIcon name={estaInadimplente ? 'warning' : 'brand'} />
                      <span>{papelUsuario}</span>
                    </div>
                  </div>
                </div>
              )}

      {estaInadimplente ? (
        isFilial ? (
          <div style={{ marginTop: '40px', padding: '40px', textAlign: 'center', backgroundColor: '#fdf3f3', border: '1px solid #f5c6cb', borderRadius: '8px' }}>
            <h2 style={{ color: '#721c24' }}>Acesso Suspenso</h2>
            <p style={{ fontSize: '16px', color: '#555', marginTop: '15px' }}>
              As funcionalidades do painel estão temporariamente bloqueadas para esta unidade.
            </p>
            <p style={{ fontSize: '16px', color: '#555' }}>
              Por favor, entre em contato com a administração da Loja Matriz para restabelecer o acesso.
            </p>
          </div>
        ) : (
          <div style={{ marginTop: '20px' }}>
            <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '15px', marginBottom: '20px', borderRadius: '5px', border: '1px solid #f5c6cb' }}>
              <strong>Acesso Bloqueado por Inadimplência!</strong> Efetue o pagamento da fatura pendente abaixo para reativar o seu painel e as suas filiais.
            </div>
            <AbaFinanceiro usuarioAtual={usuarioAtual} configGerais={configGerais} setAbaAtiva={setAbaAtiva} />
          </div>
        )
      ) : (
        <>
          {abaAtiva === 'planos' && (
  <AbaVitrinePlanos usuarioAtual={usuarioAtual} planoAtual={planoAtual} configGerais={configGerais} handleBotaoAssinar={handleBotaoAssinar} setAbaAtiva={setAbaAtiva} />
)}

          {abaAtiva === 'meusProdutos' && (
            <AbaMeuCatalogo 
            planoAtual={planoAtual} isAdmin={isAdmin} 
            setAbaAtiva={setAbaAtiva} 
            salvarProdutoPadrao={salvarProdutoPadrao} 
            handleNovoProdutoChange={handleNovoProdutoChange} 
            novoProdutoPadrao={novoProdutoPadrao} 
            tags={tags} 
            isSalvandoProduto={isSalvandoProduto} 
            produtosPadrao={produtosPadrao} 
            carregandoProdutos={carregandoProdutosPadrao}
            temMaisProdutos={temMaisProdutosPadrao}
            erroCatalogo={erroCatalogo}
            podeUsarCatalogo={podeUsarCatalogo}
            filtrarProdutos={filtrarProdutosDoCatalogo}
            carregarMaisProdutos={carregarMaisProdutosDoCatalogo}
            excluirProdutoPadrao={excluirProdutoPadrao} 
            iniciarEdicaoProduto={iniciarEdicaoProduto}
            cancelarEdicao={cancelarEdicao}
                />
          )}

          {abaAtiva === 'filiais' && (
            <AbaMinhasFiliais 
              usuarioAtual={usuarioAtual} 
              minhasFiliais={minhasFiliais} 
              configGerais={configGerais} 
              onFilialPendenteAdicionada={(filial) => {
                if (!filial?.id) return;

                setUsuarioAtual((atual) => {
                  const filaAtual = Array.isArray(atual?.filiais_pendentes_vinculo)
                    ? atual.filiais_pendentes_vinculo
                    : [];
                  const filaAtualizada = filaAtual.some((item) => item.id === filial.id)
                    ? filaAtual
                    : [...filaAtual, filial];

                  return {
                    ...atual,
                    filiais_pendentes_vinculo: filaAtualizada,
                    filiais_pendentes_pagamento: filaAtualizada.length,
                  };
                });
              }}
              onFilialPendenteRemovida={(filialId) => {
                setMinhasFiliais((atuais) => atuais.filter((filial) => filial.id !== filialId));
                setUsuarioAtual((atual) => {
                  const filaAtual = Array.isArray(atual?.filiais_pendentes_vinculo)
                    ? atual.filiais_pendentes_vinculo
                    : [];
                  const filaAtualizada = filaAtual.filter((filial) => filial.id !== filialId);

                  return {
                    ...atual,
                    filiais_pendentes_vinculo: filaAtualizada,
                    filiais_pendentes_pagamento: filaAtualizada.length,
                  };
                });
              }}
              onFiliaisAtivadas={(filiais) => {
                const idsAtivados = new Set((filiais || []).map((filial) => filial.id));
                if (!idsAtivados.size) return;

                setUsuarioAtual((atual) => ({
                  ...atual,
                  filiais_pendentes_vinculo: [],
                  filiais_pendentes_pagamento: 0,
                }));
                setMinhasFiliais((atuais) => {
                  const idsExistentes = new Set(atuais.map((filial) => filial.id));
                  const novasFiliais = (filiais || [])
                    .filter((filial) => !idsExistentes.has(filial.id))
                    .map(({ status_vinculo, ...filial }) => filial);
                  return [...atuais, ...novasFiliais];
                });
              }}
              isAdmin={isAdmin} 
            />
          )}

          {abaAtiva === 'financeiro' && (
            <AbaFinanceiro usuarioAtual={usuarioAtual} configGerais={configGerais} setAbaAtiva={setAbaAtiva} />
          )}

          {abaAtiva === 'postagemLote' && (
            !podeOperarPublicacao ? (
              <div className="routine-callout routine-callout--warm" style={{ marginTop: '14px', textAlign: 'center' }}>
                <p className="routine-callout__eyebrow">Operação diária</p>
                <h3 className="routine-callout__title">Acesso indisponível para este perfil</h3>
                <p className="routine-callout__text" style={{ maxWidth: '620px', margin: '0 auto' }}>
                  Esta área é exclusiva para os perfis com operação de publicação liberada.
                </p>
              </div>
            ) : planoAtual === 'gratuito' && !isAdmin && !isTechnical ? (
              <div className="routine-callout routine-callout--warm" style={{ marginTop: '14px', textAlign: 'center' }}>
                <p className="routine-callout__eyebrow">Operação diária</p>
                <h3 className="routine-callout__title">Ganhe horas de trabalho</h3>
                <p className="routine-callout__text" style={{ maxWidth: '620px', margin: '0 auto' }}>
                  A postagem em lote acelera a rotina do lojista que precisa publicar com velocidade e menos trabalho manual.
                  <br /><br />
                  <strong>Recurso exclusivo para assinantes.</strong> Assine o Plano Bronze ou superior para desbloquear esta área.
                </p>
                <div className="routine-actions" style={{ justifyContent: 'center', marginTop: '20px' }}>
                  <button onClick={() => setAbaAtiva('planos')} className="routine-btn routine-btn--warning">
                    Ver planos e fazer upgrade
                  </button>
                </div>
              </div>
            ) : (
              // A tela real (para assinantes e admins)
              <AbaPostagemLote
                planoAtual={planoAtual} 
                postagensWebNaSemana={postagensWebNaSemana} 
                cotaMaxWeb={cotaMaxWeb} 
                postagensMensaisGeral={postagensMensaisGeral} 
                limMensalGratuitoVisual={limMensalGratuitoVisual} 
                loteProdutos={loteProdutos} 
                handleLoteChange={handleLoteChange} 
                handleLoteMultiChange={handleLoteMultiChange} 
                produtosPadrao={produtosPadrao} 
                buscarSugestoesCatalogo={buscarSugestoesCatalogo}
                podeUsarCatalogo={podeUsarCatalogo}
                tags={tags} 
                removerLinhaLote={removerLinhaLote} 
                adicionarLinhaLote={adicionarLinhaLote} 
                isEnviandoLote={isEnviandoLote} 
                enviarLoteAoFirebase={enviarLoteAoFirebase}
                minhasFiliais={minhasFiliais} 
                usuarioAtual={usuarioAtual}
                podeSelecionarLojaNoLote={isAdmin || isTechnical}
                locaisDoLote={locaisDoLote}
                localSelecionadoNoLoteId={localSelecionadoNoLoteId}
                setLocalSelecionadoNoLoteId={setLocalSelecionadoNoLoteId}
                progressoEnvio={progressoEnvio}
                aplicarDataATodos={aplicarDataATodos}
              />
            )
          )}

          {abaAtiva === 'inicio' && (
            <AbaInicioPainel
            />
          )}
          {podeVerUsuarios && abaAtiva === 'usuarios' && (<AbaUsuarios usuarios={usuarios} removerPerfilAtivo={removerPerfilAtivo} alterarPlanoLojista={alterarPlanoLojista} alternarStatusUsuario={alternarStatusUsuario} adicionarPerfilManual={adicionarPerfilManual} somenteConsulta={!podeGerirUsuarios} />)}
          {isAdmin && abaAtiva === 'clientes' && <AbaClientes />}
          {podeVerAprovacoes && abaAtiva === 'aprovacoes' && (<AbaAprovacoes usuariosPendentes={usuariosPendentes} aprovarPerfil={aprovarPerfil} recusarPerfil={recusarPerfil} somenteConsulta={!podeGerirAprovacoes} gerenciarExclusao={gerenciarExclusaoPainel} podeExcluirConta={isAdmin} aguardandoReautenticacao={modalReautenticacaoInternaAberto} />)}
          {podeVerModeracao && abaAtiva === 'postagens' && (<AbaModeracao promocaoSelecionada={promocaoSelecionada} setPromocaoSelecionada={setPromocaoSelecionada} excluirPromocao={excluirPromocao} promocoes={promocoes} buscarPromocoes={buscarPromocoes} aprovarDenuncia={aprovarDenuncia} temMaisDados={temMaisDados} somenteConsulta={!podeModerarConteudo} />)}
          {podeVerTags && abaAtiva === 'tags' && (<AbaTags adicionarTag={adicionarTag} novaTag={novaTag} setNovaTag={setNovaTag} tags={tags} editandoId={editandoId} textoEditado={textoEditado} setTextoEditado={setTextoEditado} salvarEdicao={salvarEdicao} iniciarEdicao={iniciarEdicao} excluirTag={excluirTag} somenteConsulta={!podeGerirTags} />)}
          {podeVerRelatorios && abaAtiva === 'relatorios' && (
            isAdmin || planoAtual?.toLowerCase() === 'diamante' ? (
              <AbaRelatorios usuarioAtual={usuarioAtual} isAdmin={isAdmin} podeVerRedeCompleta={isAdmin} podeReconstruirRadar={isAdmin} tags={tags} />
            ) : (
              <div className="routine-callout routine-callout--diamond" style={{ marginTop: '14px', textAlign: 'center' }}>
                <p className="routine-callout__eyebrow">Inteligência de mercado</p>
                <h3 className="routine-callout__title">Você conhece o interesse do seu consumidor?</h3>
                <p className="routine-callout__text" style={{ maxWidth: '660px', margin: '0 auto' }}>
                  O Plano Diamante libera uma visão mais estratégica da demanda regional e ajuda a matriz a tomar decisões com mais clareza.
                  <br /><br />
                  Em operações com matriz e filiais, os dados ficam consolidados em um único painel para a unidade principal.
                </p>
                <div className="routine-actions" style={{ justifyContent: 'center', marginTop: '20px' }}>
                  <button onClick={() => setAbaAtiva('planos')} className="routine-btn routine-btn--diamond">
                    Saber mais sobre o Plano Diamante
                  </button>
                </div>
              </div>
            )
          )}
          {podeVerConfiguracoes && abaAtiva === 'configuracoes' && (
            <AbaConfiguracoes
              configGerais={configGerais}
              handleConfigChange={handleConfigChange}
              monitorResumo={monitorResumo}
              monitoramentoRetorno={monitoramentoRetorno}
              monitoramentoCarregando={monitoramentoCarregando}
              recarregarMonitoramento={atualizarMonitoramentoAgora}
              somenteConsulta={!podeGerirConfiguracoes}
            />
          )}
          {podeVerAfiliados && abaAtiva === 'afiliados' && (<AbaAfiliados configAfiliados={configAfiliados} handleAfiliadosChange={handleAfiliadosChange} somenteConsulta={!podeGerirAfiliados} />)}
          {podeVerLimpeza && abaAtiva === 'limpeza' && (<AbaLimpeza opcoesLimpeza={opcoesLimpeza} handleCheckboxLimpeza={handleCheckboxLimpeza} gerenciarLimpeza={gerenciarLimpezaPainel} somenteConsulta={!podeGerirLimpeza} aguardandoReautenticacao={modalReautenticacaoInternaAberto} />)}
          {podeVerManutencaoLojas && abaAtiva === 'manutencao_lojas' && <AbaManutencaoLojas somenteConsulta={!podeGerirManutencaoLojas} />}
          {podeVerLocais && abaAtiva === 'locais' && <AbaGestaoLocais somenteConsulta={!podeGerirLocais} />}
        </>
      )}

      {/* Rodapé */}
      <div className="panel-footer">
        <span className="panel-footer__link" onClick={() => setMostrarPrivacidade(true)}>
          Política de Privacidade
        </span>
        <span className="panel-footer__separator">|</span>
        <span className="panel-footer__link" onClick={() => setMostrarTermos(true)}>
          Termos e Diretrizes
        </span>
      </div>

      {/* Janela flutuante de termos */}
          </section>
          </main>
        </div>
      </div>

      <ModalFaturaGerada
        linkPagamento={faturaPlanoParaAbrir}
        onFechar={() => setFaturaPlanoParaAbrir(null)}
      />

      <ModalReautenticacao
        aberto={modalReautenticacaoInternaAberto}
        titulo="Confirmar ação administrativa"
        descricao="Confirme sua senha atual antes de aprovar acessos, alterar usuários ou excluir uma conta no painel interno."
        textoBotao="Confirmar identidade"
        onFechar={cancelarReautenticacaoInterna}
        onConfirmado={concluirReautenticacaoInterna}
      />

      {mostrarTermos && (
        <div className="panel-modal">
          <div className="panel-modal__dialog">
            <div className="panel-modal__header">
              <p className="panel-modal__eyebrow">Diretrizes do ecossistema</p>
              <h3 className="panel-modal__title">Termos de Uso e Diretrizes</h3>
            </div>

            <div className="panel-modal__content">
              <h4 className="panel-modal__section-title">Parte 1: Regras gerais da comunidade</h4>
              <p><strong>1. Tolerância zero e respeito:</strong> É estritamente proibido conteúdo sexual, discurso de ódio, discriminação, racismo ou ofensas. É terminantemente proibido o anúncio de serviços ilícitos, armas ou drogas. No PromoAlert prezamos pelo respeito: seja educado com o próximo e use os comentários com consciência.</p>
              <p><strong>2. Radar de ofertas, não classificados:</strong> O nosso foco é gerar economia real no varejo. Permitimos ofertas do varejo local e on-line. Não permitimos aluguel ou venda de imóveis, veículos, desapego de itens pessoais usados e anúncio de prestação de serviços.</p>
              <p><strong>3. Clareza, transparência e anti-spam:</strong> O preço digitado na plataforma deve ser o preço final pago no caixa. Caso a oferta tenha uma data de validade, esta deve ser obrigatoriamente informada. Não repita a publicação da mesma oferta várias vezes.</p>
              <p><strong>4. Validade e exclusão automática:</strong> As ofertas possuem um prazo de validade. Após esse período, elas são automaticamente excluídas do nosso banco de dados, garantindo um feed limpo e ofertas atuais.</p>

              <h4 className="panel-modal__section-title">Parte 2: Condições comerciais (lojistas)</h4>
              <p><strong>5. Natureza do serviço e veracidade:</strong> O PromoAlert atua como vitrine tecnológica. Nós não vendemos produtos. O lojista oficial atesta e garante a veracidade do preço e do estoque anunciado. O PromoAlert isenta-se de qualquer responsabilidade perante o consumidor final por publicidade enganosa ou ruptura de estoque por parte do lojista.</p>
              <p><strong>6. Política de assinaturas e reembolsos:</strong> As assinaturas dos planos são serviços digitais de disponibilização imediata. Não haverá lugar a reembolso proporcional ou total de mensalidades após a utilização da cota de publicações do respectivo mês.</p>
              <p><strong>7. Violação de diretrizes:</strong> O PromoAlert reserva-se o direito de suspender ou excluir contas de lojistas que pratiquem fraude, spam ou infrinjam regras, sem direito a reembolso.</p>
            </div>

            <div className="panel-modal__actions">
              <button type="button" className="panel-modal__button panel-modal__button--danger" onClick={() => setMostrarTermos(false)}>
                Entendi e concordo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Janela flutuante de privacidade */}
      {mostrarPrivacidade && (
        <div className="panel-modal">
          <div className="panel-modal__dialog">
            <div className="panel-modal__header">
              <p className="panel-modal__eyebrow">Transparência e proteção</p>
              <h3 className="panel-modal__title">Política de Privacidade</h3>
            </div>

            <div className="panel-modal__content">
              <p>A sua privacidade é a nossa prioridade. O PromoAlert coleta, usa e protege os seus dados em total conformidade com a Lei Geral de Proteção de Dados (LGPD).</p>

              <h4 className="panel-modal__section-title">1. Quais dados coletamos e como usamos?</h4>
              <p>Dados de cadastro: nome e e-mail, utilizados exclusivamente para a criação e segurança da sua conta.</p>
              <p>Localização (GPS): utilizada apenas para definir geograficamente as ofertas mais próximas de você e garantir o funcionamento do radar na sua região.</p>
              <p>Dados de navegação: cliques e interesses em categorias para personalizar o seu feed.</p>

              <h4 className="panel-modal__section-title">2. Compartilhamento e estatísticas</h4>
              <p>Os seus dados pessoais são estritamente confidenciais e não são compartilhados com terceiros. O sistema apenas agrupa dados de navegação de forma totalmente anônima para gerar estatísticas de demanda por região, incentivando os lojistas a publicarem ofertas melhores para a sua cidade.</p>
              <p>O compartilhamento operacional ocorre apenas com os nossos parceiros de tecnologia, como servidores e pagamentos, para manter o sistema no ar.</p>

              <h4 className="panel-modal__section-title">3. Os seus direitos</h4>
              <p>Você tem controle total. A qualquer momento no aplicativo pode editar os seus dados, revogar as permissões de GPS ou excluir permanentemente a sua conta e histórico do nosso banco de dados.</p>
            </div>

            <div className="panel-modal__actions">
              <button type="button" className="panel-modal__button panel-modal__button--primary" onClick={() => setMostrarPrivacidade(false)}>
                Ciente
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Dashboard;
